import { DateTime } from 'luxon'
import {
  formatCoachProfileFacilityLabel,
} from '@lib/pga-coach/offering-search'
import isPgaLink from '@utils/isPgaLink'

export const BOOKING_WIDGET_HEADING = 'Book a lesson'
export const BOOKING_WIDGET_HEADING_ID = 'coach-profile-booking-widget-heading'
export const BOOKING_WIDGET_TEST_ID = 'coach-profile-booking-widget'
export const VIEW_FULL_AVAILABILITY_LABEL = 'View full availability'
export const FULL_CALENDAR_LABEL = 'All Open Times'
export const SELECT_A_TIME_LABEL = 'Select a time'
export const NO_TIMES_LABEL = 'No times available'
export const LESSON_PACKAGE_OFFERING_SUFFIX = ' (Lesson Package)'

export const DEFAULT_PREFERRED_MINUTES = 9 * 60
export const PREFERRED_TIME_STEP_MINUTES = 30
export const PREFERRED_TIME_START_MINUTES = 6 * 60
export const PREFERRED_TIME_END_MINUTES = 20 * 60
export const BOOKING_DATE_HORIZON_DAYS = 28
export const AVAILABLE_SLOT_LIMIT = 4
export const AVAILABLE_TIMES_QUERY_LIMIT = 96
export const DEFAULT_TIMEZONE = 'UTC'

const AVAILABILITY_WINDOW_IDS = new Set([
  'this_week',
  'next_week',
  'this_month',
  'next_month',
])

export const getBookingNow = () => DateTime.utc()

const parseDateTime = (value, zone) => {
  if (!value) return null
  if (value instanceof DateTime) return value.isValid ? value : null
  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value, { zone: 'utc' })
    if (!dt.isValid) return null
    return zone ? dt.setZone(zone) : dt
  }
  const dt = DateTime.fromISO(String(value), { zone: 'utc', setZone: true })
  if (!dt.isValid) return null
  return zone ? dt.setZone(zone) : dt
}

export const facilityTimezoneFromLessonTypes = (lessonTypes, facilityId) => {
  const facilities = lessonTypes?.[0]?.facilities || []
  const match = facilities.find((facility) => (
    facilityId && String(facility?.id) === String(facilityId)
  ))
  return match?.timezone || facilities[0]?.timezone || null
}

export const offeringTimezone = (offering) => (
  offering?.timezone || DEFAULT_TIMEZONE
)

export const offeringHasBookableAvailability = (offering) => {
  if (offering?.hasAvailability) return true
  if (offering?.nextAvailableAt) return true
  const windows = offering?.availabilityWindows || offering?.availability_windows || []
  return (Array.isArray(windows) ? windows : []).some((windowId) => (
    AVAILABILITY_WINDOW_IDS.has(windowId)
  ))
}

export const toUtcIsoNoMs = (dt) => {
  if (!dt || !dt.isValid) return null
  return dt.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'")
}

export const getFutureSlots = (slots, timezone, now = getBookingNow()) => (
  (slots || []).filter((slot) => {
    const utc = parseDateTime(slot?.startDateTime)
    if (!utc || utc <= now) return false
    const zone = timezone || DEFAULT_TIMEZONE
    const zoned = parseDateTime(slot.startDateTime, zone)
    if (!zoned) return false
    const horizonStart = now.setZone(zone).startOf('day')
    const horizonEnd = horizonStart.plus({ days: BOOKING_DATE_HORIZON_DAYS })
    return zoned >= horizonStart && zoned < horizonEnd
  })
)

export const findEarliestBookableSlot = (
  slots,
  timezone,
  now = getBookingNow(),
) => {
  const zone = timezone || DEFAULT_TIMEZONE
  const sorted = getFutureSlots(slots, zone, now)
    .map((slot) => ({
      slot,
      zoned: parseDateTime(slot?.startDateTime, zone),
    }))
    .filter((entry) => entry.zoned)
    .sort((left, right) => left.zoned.toMillis() - right.zoned.toMillis())
  return sorted[0]?.slot || null
}

/**
 * Private lessons a golfer can book from the profile widget: not
 * access-code gated, and Algolia reports availability (`hasAvailability`,
 * `nextAvailableAt`, or `availabilityWindows` such as `this_week`).
 * Widget offering rows use {@link getBookablePrivateLessonOfferingRows}
 * (one row per Algolia hit / facility). {@link getBookablePrivateLessonOfferings}
 * dedupes by `lessonTypeId` for coarse “has any bookable private lesson” checks.
 */
export const formatBookingOfferingLabel = (offering) => {
  const title = String(offering?.title || '').trim()
  if (!title) return ''
  return offering?.hasLessonPacks
    ? `${title}${LESSON_PACKAGE_OFFERING_SUFFIX}`
    : title
}

/** All bookable private rows for one lesson type (one per facility when expanded). */
export const getFacilityOfferingsForLessonType = (
  offerings,
  lessonTypeId,
) => {
  const id = String(lessonTypeId || '').trim()
  if (!id) return []
  return (offerings || []).filter((offering) => {
    if (offering?.format !== 'private') return false
    if (offering?.requiresAccessCode) return false
    if (!offeringHasBookableAvailability(offering)) return false
    return String(offering?.lessonTypeId || offering?.objectID || '').trim() === id
  })
}

const formatGraphqlFacilityLabel = (facility = {}) => {
  const venueName = String(facility?.name || '').trim()
  const cityState = [facility?.city, facility?.state]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
    .join(', ')
  const parts = [venueName, cityState].filter(Boolean)
  return parts.length ? parts.join(', ') : null
}

export const resolveBookingLocationLabel = (
  locationId,
  facilityOfferings = [],
  graphqlFacilities = [],
) => {
  const id = String(locationId || '').trim()
  if (!id) return null
  const offering = facilityOfferings.find((row) => (
    String(row?.facilityId || '').trim() === id
  ))
  const fromOffering = formatCoachProfileFacilityLabel(offering)
  if (fromOffering) return fromOffering
  const facility = graphqlFacilities.find((row) => (
    String(row?.id || '').trim() === id
  ))
  return formatGraphqlFacilityLabel(facility)
}

/**
 * Locations with Coach Tools slots in the booking horizon. Omits facilities
 * that only have Algolia availability hints when `requireSlots` is true.
 */
export const buildBookingLocationOptions = ({
  slots = [],
  facilityOfferings = [],
  graphqlFacilities = [],
  timezone,
  now = getBookingNow(),
  requireSlots = false,
} = {}) => {
  const zone = timezone || DEFAULT_TIMEZONE
  const locationIds = new Set()
  getFutureSlots(slots, zone, now).forEach((slot) => {
    const locationId = String(slot?.locationId || '').trim()
    if (locationId) locationIds.add(locationId)
  })
  if (!locationIds.size && !requireSlots) {
    const fallbackId = String(facilityOfferings[0]?.facilityId || '').trim()
    if (fallbackId) locationIds.add(fallbackId)
  }
  return [...locationIds]
    .map((value) => ({
      value,
      label: resolveBookingLocationLabel(
        value,
        facilityOfferings,
        graphqlFacilities,
      ),
    }))
    .filter((option) => option.label)
    .sort((left, right) => left.label.localeCompare(right.label))
}

export const filterSlotsByLocationId = (slots, locationId) => {
  const id = String(locationId || '').trim()
  if (!id) return slots || []
  return (slots || []).filter((slot) => String(slot?.locationId || '').trim() === id)
}

export const getOfferingBookingKey = (offering) => {
  const objectId = String(offering?.objectID || '').trim()
  if (objectId) return objectId
  const lessonTypeId = String(offering?.lessonTypeId || '').trim()
  const facilityId = String(offering?.facilityId || '').trim()
  return `${lessonTypeId}:${facilityId || 'default'}`
}

/** One widget row per bookable private offering (including each facility). */
export const getBookablePrivateLessonOfferingRows = (offerings = []) => {
  const seen = new Set()
  const rows = []
  offerings.forEach((offering) => {
    if (offering?.format !== 'private') return
    if (offering?.requiresAccessCode) return
    if (!offeringHasBookableAvailability(offering)) return
    const lessonTypeId = String(offering?.lessonTypeId || offering?.objectID || '').trim()
    if (!lessonTypeId) return
    const key = getOfferingBookingKey({ ...offering, lessonTypeId })
    if (seen.has(key)) return
    seen.add(key)
    rows.push({ ...offering, lessonTypeId })
  })
  return rows.sort((left, right) => (
    String(left.title || '').localeCompare(String(right.title || ''))
  ))
}

export const getUniqueBookableLessonTypeIds = (rows = []) => (
  [...new Set(rows.map((row) => String(row?.lessonTypeId || '').trim()).filter(Boolean))]
)

export const getBookableOfferingsAtLocation = (rows, locationId) => {
  const id = String(locationId || '').trim()
  if (!id) return []
  return (rows || [])
    .filter((row) => String(row?.facilityId || '').trim() === id)
    .sort((left, right) => (
      String(left.title || '').localeCompare(String(right.title || ''))
    ))
}

export const offeringHasBookableSlotsAtLocation = ({
  offering,
  availabilityByLessonType = {},
  locationId,
  timezone,
  now = getBookingNow(),
} = {}) => {
  const lessonTypeId = String(offering?.lessonTypeId || '').trim()
  const facilityId = String(locationId || offering?.facilityId || '').trim()
  if (!lessonTypeId || !facilityId) return false
  const slots = filterSlotsByLocationId(
    availabilityByLessonType[lessonTypeId]?.availableTimes || [],
    facilityId,
  )
  return getFutureSlots(slots, timezone || offeringTimezone(offering), now).length > 0
}

/** Default offering at a facility: earliest Coach Tools slot, else first title. */
export const pickDefaultOfferingAtLocation = ({
  offeringsAtLocation = [],
  availabilityByLessonType = {},
  locationId,
  timezone,
  now = getBookingNow(),
} = {}) => {
  if (!offeringsAtLocation.length) return ''
  const zone = timezone || offeringTimezone(offeringsAtLocation[0])
  const facilityId = String(locationId || offeringsAtLocation[0]?.facilityId || '').trim()

  let bestOffering = null
  let bestMillis = null

  offeringsAtLocation.forEach((offering) => {
    const lessonTypeId = String(offering?.lessonTypeId || '').trim()
    if (!lessonTypeId) return
    const slots = filterSlotsByLocationId(
      availabilityByLessonType[lessonTypeId]?.availableTimes || [],
      facilityId,
    )
    const earliest = findEarliestBookableSlot(slots, zone, now)
    if (!earliest) return
    const zoned = parseDateTime(earliest.startDateTime, zone)
    if (!zoned) return
    const millis = zoned.toMillis()
    if (bestMillis == null || millis < bestMillis) {
      bestMillis = millis
      bestOffering = offering
    }
  })

  if (bestOffering) return getOfferingBookingKey(bestOffering)
  return getOfferingBookingKey(offeringsAtLocation[0])
}

export const mergeAvailabilitySlotsByLessonType = (byLessonType = {}) => {
  const merged = []
  Object.entries(byLessonType || {}).forEach(([lessonTypeId, lessonType]) => {
    (lessonType?.availableTimes || []).forEach((slot) => {
      merged.push({ ...slot, lessonTypeId })
    })
  })
  return merged
}

export const collectGraphqlFacilities = (byLessonType = {}) => {
  const byId = new Map()
  Object.values(byLessonType || {}).forEach((lessonType) => {
    (lessonType?.facilities || []).forEach((facility) => {
      const id = String(facility?.id || '').trim()
      if (id) byId.set(id, facility)
    })
  })
  return [...byId.values()]
}

export const getBookablePrivateLessonOfferings = (offerings = []) => {
  const byLessonType = new Map()
  getBookablePrivateLessonOfferingRows(offerings).forEach((offering) => {
    const lessonTypeId = String(offering?.lessonTypeId || '').trim()
    if (!lessonTypeId || byLessonType.has(lessonTypeId)) return
    byLessonType.set(lessonTypeId, offering)
  })
  return [...byLessonType.values()]
}

/**
 * PGA Coach scheduler URL for this profile (`my.pga.com/.../schedule`).
 * External-tool booking URLs return null so the widget stays hidden.
 */
export const getCoachPrivateScheduleUrl = (coach) => {
  const raw = String(coach?.bookingUrl || coach?.bookingURL || '').trim()
  if (raw) {
    try {
      if (!isPgaLink(raw)) return null
      const parsed = new URL(raw)
      parsed.search = ''
      parsed.hash = ''
      return parsed.toString().replace(/\/$/, '')
    } catch {
      return null
    }
  }
  const slug = String(coach?.customSlug || '').trim()
  const base = String(process.env.MY_PGA_COM_URL || '').replace(/\/$/, '')
  if (!base || !slug) return null
  return `${base}/coach/${slug}/schedule`
}

export const buildPrivateLessonScheduleUrl = ({
  scheduleUrl,
  lessonTypeId,
  locationId,
  startDateTime,
  selectedDay,
  hasLessonPacks = false,
} = {}) => {
  if (!scheduleUrl) return null
  const encode = (value) => encodeURIComponent(String(value).trim()).replace(/%3A/gi, ':')
  const params = []
  if (lessonTypeId) params.push(`lessonTypeId=${encode(lessonTypeId)}`)
  if (locationId) params.push(`locationId=${encode(locationId)}`)
  if (selectedDay) {
    params.push(`selectedDay=${encode(selectedDay)}`)
  } else if (startDateTime) {
    const normalized = toUtcIsoNoMs(parseDateTime(startDateTime)) || startDateTime
    params.push(`startDateTime=${encode(normalized)}`)
  }
  if (hasLessonPacks) params.push('isLessonPacks=true')
  if (!params.length) return scheduleUrl
  const joiner = scheduleUrl.includes('?') ? '&' : '?'
  return `${scheduleUrl}${joiner}${params.join('&')}`
}

/** View full availability — `{MY_PGA_COM_URL}/coach/{slug}/schedule` with `selectedDay`. */
export const buildViewFullAvailabilityScheduleUrl = ({
  scheduleUrl,
  lessonTypeId,
  locationId,
  selectedDay,
  hasLessonPacks = false,
} = {}) => (
  buildPrivateLessonScheduleUrl({
    scheduleUrl,
    lessonTypeId,
    locationId,
    selectedDay,
    hasLessonPacks,
  })
)

/** Start-of-day in the offering timezone for PGA Coach `/schedule` calendar focus. */
export const buildScheduleCalendarStartDateTime = (dateIso, timezone) => {
  const zone = timezone || DEFAULT_TIMEZONE
  const start = DateTime.fromISO(String(dateIso || ''), { zone }).startOf('day')
  if (!start.isValid) return null
  return toUtcIsoNoMs(start)
}

const utcDayRangePadHours = (zone) => (zone === DEFAULT_TIMEZONE ? 14 : 0)

export const getSelectedDayUtcRange = (dateIso, timezone) => {
  const zone = timezone || DEFAULT_TIMEZONE
  const start = DateTime.fromISO(String(dateIso || ''), { zone }).startOf('day')
  if (!start.isValid) return null
  // Algolia private offerings often omit timezone. Pad a UTC day so a
  // US facility-local calendar day still fits the Coach Tools window.
  const padHours = utcDayRangePadHours(zone)
  return {
    startDateTime: toUtcIsoNoMs(start.minus({ hours: padHours })),
    endDateTime: toUtcIsoNoMs(start.plus({ days: 1, hours: padHours })),
  }
}

/** Coach Tools window for bookable dates shown in the widget (next 4 weeks). */
export const getBookingHorizonUtcRange = (timezone, now = getBookingNow()) => {
  const zone = timezone || DEFAULT_TIMEZONE
  const start = now.setZone(zone).startOf('day')
  if (!start.isValid) return null
  const padHours = utcDayRangePadHours(zone)
  const end = start.plus({ days: BOOKING_DATE_HORIZON_DAYS })
  return {
    startDateTime: toUtcIsoNoMs(start.minus({ hours: padHours })),
    endDateTime: toUtcIsoNoMs(end.plus({ hours: padHours })),
  }
}

export const roundMinutesToStep = (
  minutes,
  step = PREFERRED_TIME_STEP_MINUTES,
) => {
  const rounded = Math.round(Number(minutes) / step) * step
  return Math.min(
    PREFERRED_TIME_END_MINUTES,
    Math.max(PREFERRED_TIME_START_MINUTES, rounded),
  )
}

export const buildPreferredTimeOptions = () => {
  const options = []
  for (
    let minutes = PREFERRED_TIME_START_MINUTES;
    minutes <= PREFERRED_TIME_END_MINUTES;
    minutes += PREFERRED_TIME_STEP_MINUTES
  ) {
    const hour = Math.floor(minutes / 60)
    const minute = minutes % 60
    const dt = DateTime.fromObject({ hour, minute }, { zone: 'utc' })
    options.push({
      value: String(minutes),
      minutes,
      label: dt.toFormat('h:mm a'),
    })
  }
  return options
}

export const PREFERRED_TIME_OPTIONS = buildPreferredTimeOptions()

export const formatBookingDateLabel = (dateIso, timezone) => {
  const zone = timezone || DEFAULT_TIMEZONE
  const dt = DateTime.fromISO(String(dateIso || ''), { zone })
  if (!dt.isValid) return ''
  return dt.toFormat('ccc, LLL d, yyyy')
}

/** Year-less label for the closed date field, which shares a row with the time. */
export const formatBookingDateShortLabel = (dateIso, timezone) => {
  const zone = timezone || DEFAULT_TIMEZONE
  const dt = DateTime.fromISO(String(dateIso || ''), { zone })
  if (!dt.isValid) return ''
  return dt.toFormat('ccc, LLL d')
}

export const buildDateOptionsFromSlots = (
  slots,
  timezone,
  now = getBookingNow(),
) => {
  const zone = timezone || DEFAULT_TIMEZONE
  const dates = new Set()
  getFutureSlots(slots, zone, now).forEach((slot) => {
    const dt = parseDateTime(slot.startDateTime, zone)
    if (dt) dates.add(dt.toISODate())
  })
  return [...dates].sort().map((value) => ({
    value,
    label: formatBookingDateLabel(value, zone),
  }))
}

export const resolveNextAvailableFromSlots = (
  slots,
  timezone,
  now = getBookingNow(),
) => {
  const zone = timezone || DEFAULT_TIMEZONE
  const sorted = getFutureSlots(slots, zone, now)
    .map((slot) => parseDateTime(slot.startDateTime, zone))
    .filter(Boolean)
    .sort((left, right) => left.toMillis() - right.toMillis())
  const first = sorted[0]
  if (!first) {
    const zonedNow = now.setZone(zone)
    return {
      dateIso: zonedNow.toISODate(),
      preferredMinutes: DEFAULT_PREFERRED_MINUTES,
      timezone: zone,
    }
  }
  return {
    dateIso: first.toISODate(),
    preferredMinutes: roundMinutesToStep(first.hour * 60 + first.minute),
    timezone: zone,
  }
}

export const resolveOfferingDefaults = (offering, now = getBookingNow()) => {
  const zone = offeringTimezone(offering)
  const zonedNow = now.setZone(zone)
  const next = parseDateTime(offering?.nextAvailableAt, zone)
  const useNext = Boolean(next && next > zonedNow)
  const dateSource = useNext ? next : zonedNow
  const preferredMinutes = useNext
    ? roundMinutesToStep(next.hour * 60 + next.minute)
    : DEFAULT_PREFERRED_MINUTES
  return {
    dateIso: dateSource.toISODate(),
    preferredMinutes,
    timezone: zone,
  }
}

export const formatSlotTimeLabel = (startDateTime, timezone) => {
  const dt = parseDateTime(startDateTime, timezone || DEFAULT_TIMEZONE)
  if (!dt) return ''
  return dt.toFormat('h:mm a')
}

const slotMinutesFromMidnight = (slot, timezone) => {
  const dt = parseDateTime(slot?.startDateTime, timezone || DEFAULT_TIMEZONE)
  if (!dt) return null
  return dt.hour * 60 + dt.minute
}

export const filterSlotsOnDate = (
  slots,
  dateIso,
  timezone,
  now = getBookingNow(),
) => (
  (slots || []).filter((slot) => {
    const dt = parseDateTime(slot?.startDateTime, timezone || DEFAULT_TIMEZONE)
    if (!dt) return false
    if (dt.toISODate() !== dateIso) return false
    const utc = parseDateTime(slot.startDateTime)
    return utc && utc > now
  })
)

export const pickSlotsNearPreferredTime = (
  slots,
  preferredMinutes,
  timezone,
  limit = AVAILABLE_SLOT_LIMIT,
) => {
  const preferred = Number(preferredMinutes)
  const ranked = (slots || [])
    .map((slot) => {
      const minutes = slotMinutesFromMidnight(slot, timezone)
      if (minutes == null) return null
      return {
        slot,
        minutes,
        diff: Math.abs(minutes - preferred),
      }
    })
    .filter(Boolean)
    .sort((left, right) => left.diff - right.diff || left.minutes - right.minutes)
  return ranked
    .slice(0, limit)
    .sort((left, right) => left.minutes - right.minutes)
    .map((entry) => entry.slot)
}
